import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getMinimalMRTOptions } from "../../../../mantine/getMinimalMRTOptions";
import { roundedNumericFormatter } from "../../../../utils/RoundedNumberFormat";
import type { Paragraph6EEGCreditData } from "../../Paragraph6.types";

interface Paragraph6ParticipationTableProps {
  creditData: Paragraph6EEGCreditData;
}

interface ParticipationTableRow {
  label: string;
  kwh: string | null;
  eur: string | null;
  isBold?: boolean;
  isIndented?: boolean;
}

function Paragraph6ParticipationTable({
  creditData
}: Paragraph6ParticipationTableProps) {
  const tableData: ParticipationTableRow[] = useMemo(
    () => [
      {
        label: "Tatsächlich eingespeiste Strommenge",
        kwh: creditData.kwh_einspeisung,
        eur: creditData.eur_einspeisung
      },
      {
        label: "Davon rückerstattungsfähig",
        kwh: creditData.kwh_einspeisung_erstattungsfaehig,
        eur: creditData.eur_einspeisung_erstattungsfaehig,
        isIndented: true
      },
      {
        label: "Davon nicht rückerstattungsfähig",
        kwh: creditData.kwh_einspeisung_nicht_erstattungsfaehig,
        eur: creditData.eur_einspeisung_nicht_erstattungsfaehig,
        isIndented: true
      },
      {
        label: "Fiktive Strommenge",
        kwh: creditData.kwh_fiktiv,
        eur: creditData.eur_fiktiv
      },
      {
        label: "Gesamte Strommenge",
        kwh: creditData.kwh_total,
        eur: creditData.eur_total,
        isBold: true
      }
    ],
    [creditData]
  );

  const columns = useMemo<Array<MRT_ColumnDef<ParticipationTableRow>>>(
    () => [
      {
        accessorKey: "label",
        header: "Strommengen",
        Cell: ({ row }) => (
          <span
            style={{
              paddingLeft: row.original.isIndented ? "2rem" : "0",
              fontWeight: row.original.isBold ? 700 : "normal"
            }}
          >
            {row.original.label}
          </span>
        )
      },
      {
        accessorKey: "kwh",
        header: "Beteiligung Strommenge",
        mantineTableHeadCellProps: {
          align: "right",
          ta: "right"
        },
        mantineTableBodyCellProps: {
          align: "right",
          ta: "right"
        },
        Cell: ({ row }) => (
          <span style={{ fontWeight: row.original.isBold ? 700 : "normal" }}>
            {row.original.kwh === null
              ? "liegt noch nicht vor"
              : roundedNumericFormatter(row.original.kwh, {
                  suffix: " kWh",
                  fixedDecimalScale: true
                })}
          </span>
        )
      },
      {
        accessorKey: "eur",
        header: "Beteiligung Betrag",
        mantineTableHeadCellProps: {
          align: "right",
          ta: "right"
        },
        mantineTableBodyCellProps: {
          align: "right",
          ta: "right"
        },
        Cell: ({ row }) => (
          <span style={{ fontWeight: row.original.isBold ? 700 : "normal" }}>
            {row.original.eur === null
              ? "liegt noch nicht vor"
              : roundedNumericFormatter(row.original.eur, {
                  suffix: " €",
                  fixedDecimalScale: true
                })}
          </span>
        )
      }
    ],
    []
  );

  const table = useMantineReactTable<ParticipationTableRow>({
    ...getMinimalMRTOptions<ParticipationTableRow>(),
    columns,
    data: tableData
  });

  return <MantineReactTable table={table} />;
}

export { Paragraph6ParticipationTable };
